import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getCompaniesQuery, getCompanyCategoriesQuery, getCompanyTypesQuery, getAdminableBookletsQuery, getBookletsQuery } from '../../../queries/queries';
import { LoadingTable, EditButton, sortList, SortButton, TableSection } from '../../../common/index';
import CompanyForm from './CompaniesForm';
import Sort from '../../../img/sort.svg';
import CompaniesTableBooklet from './CompaniesTableBooklet';
import CompanyTableItem from './CompanyTableItem';
import { useZoneContext } from '../../../context/ZoneContext';
import { CompaniesTableProvider, useCompaniesTableContext } from '../../../context/CompaniesTableContext';
import Select from 'react-select';
import { useQuery } from '@apollo/client';
import FilterCompaniesSection from './FilterCompaniesSection';
import { getBatchCompanyBookletReviewsQuery } from '../../../queries/bookletCompanyReview';


function CompaniesTableView(props) {
  const { zoneId } = props;
  const { data: companiesData, loading: companiesLoading, error: companiesError } = useQuery(getCompaniesQuery, { variables: { zoneId }, fetchPolicy: 'network-only' });
  const { data: adminableBookletsData } = useQuery(getAdminableBookletsQuery, { variables: { zoneId }, fetchPolicy: 'network-only' });
  const { data: categoriesQuery } = useQuery(getCompanyCategoriesQuery);
  const { data: typesQuery } = useQuery(getCompanyTypesQuery);

  const { filteredBooklets, setCompanies, setAdminableBooklets, setFilteredBooklets, showAllBooklets, zone, setAllBooklets } = useZoneContext();
  const { setTotalCompaniesCount, totalUsedCoupons } = useCompaniesTableContext();

  const [selectedOption, setSelectedOption] = useState(null);
  const [editing, setEditing] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState(1);
  const [checked, setChecked] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const sort = { sortBy, sortOrder, setSortBy, setSortOrder };

  const { data: reviewsData } = useQuery(getBatchCompanyBookletReviewsQuery, {
    variables: {
      companyIds: companiesData?.companies.map(c => c._id),
      bookletIds: filteredBooklets.map(b => b._id)
    },
    skip: companiesData?.companies.length === 0 || filteredBooklets.length === 0
  });

  useEffect(() => {
    setAllBooklets(adminableBookletsData?.booklets);
  }, [adminableBookletsData]);

  useEffect(() => {
    if (companiesData?.companies) {
      setCompanies(companiesData.companies);
      setTotalCompaniesCount(companiesData.companies.length);
    }
  }, [companiesData, setCompanies, setTotalCompaniesCount]);

  useEffect(() => {
    if (adminableBookletsData?.booklets) {
      // Step 1: Find booklets that exist in both adminableBooklets and filteredBooklets
      // This ensures we only update booklets that the user has selected to view
      const updatedBooklets = adminableBookletsData.booklets.filter(newBooklet =>
        filteredBooklets.some(existingBooklet => existingBooklet._id === newBooklet._id)
      );

      setFilteredBooklets(prev => {
        // Step 2a: Handle initial load
        // When there are no filtered booklets yet (prev.length === 0),
        // apply the default filtering based on date and zone
        if (prev.length === 0) {
          const today = new Date();
          return showAllBooklets
            ? adminableBookletsData.booklets
            : adminableBookletsData.booklets.filter(booklet =>
              booklet.zoneId === zone?._id && new Date(booklet.validFrom) > today
            );
        }

        // Step 2b: Handle updates to existing booklets
        // Map through current filtered booklets and update their data
        // while maintaining the current selection
        return prev.map(prevBooklet => {
          const updatedBooklet = updatedBooklets.find(newBooklet => newBooklet._id === prevBooklet._id);
          return updatedBooklet || prevBooklet;
        });
      });

      // Step 3: Update the complete list of adminable booklets
      setAdminableBooklets(adminableBookletsData.booklets);
    }
  }, [adminableBookletsData, showAllBooklets, zone]);

  function populateEditableCheckboxes() {
    setChecked([]);
    if (adminableBookletsData?.booklets) {
      adminableBookletsData.booklets.forEach((bookletObj) => {
        if (bookletObj.couponIds) {
          const couponIds = bookletObj.couponIds;
          const bookletId = bookletObj._id;
          if (couponIds.length > 0) {
            couponIds.forEach((coupon) => {
              let id = `${bookletId}-${coupon}`;
              setChecked((prevChecked) => [...prevChecked, id])
            })
          }
        }
      })
    }
  }

  useEffect(() => {
    populateEditableCheckboxes();
  }, [adminableBookletsData])

  const handleSelectChange = (option) => {
    if (option) {
      const selectedBooklet = adminableBookletsData.booklets.find(booklet => booklet._id === option.value);
      setFilteredBooklets(prev => [...prev, selectedBooklet]);
      setSelectedOption(null);
    }
  };

  const bookletOptions = adminableBookletsData?.booklets ? adminableBookletsData?.booklets
    .filter(booklet =>
      // Filter out booklets that exist in filteredBooklets
      !filteredBooklets.some(filtered => filtered._id === booklet._id)
    )
    .map((booklet) => ({
      value: booklet._id,
      label: new Date(booklet?.validFrom).toLocaleDateString('sv-SE') + ' - ' + booklet.title,
      validFrom: new Date(booklet?.validFrom)
    }))
    .sort((a, b) => b.validFrom - a.validFrom) : [];

  const handleFilterChange = (filteredCompanies) => {
    setFilteredCompanies(filteredCompanies);
  };

  const content = useMemo(() => ({ zoneId }), [zoneId]);

  return (
    <TableSection>
      {companiesData?.companies &&
        <FilterCompaniesSection
          activeBooklets={filteredBooklets}
          companies={companiesData?.companies}
          reviews={reviewsData?.batchBookletCompanyReviews}
          zoneId={zoneId}
          setFilteredCompanies={handleFilterChange}
        />
      }
      <div className="table-header">
        <h2>Företag</h2>
        <EditButton size="sm add" setEditing={() => setEditing('new')}>Lägg till</EditButton>
        <Select
          value={selectedOption}
          options={bookletOptions}
          onChange={handleSelectChange}
          placeholder="Visa häfte"
          isSearchable
          styles={{
            container: (base) => ({
              ...base,
              marginLeft: 'auto',
              marginRight: filteredBooklets.length * 94 + 8,
            }),
            control: (base) => ({
              ...base,
              width: 280,
            }),
            menuList: (base) => ({
              ...base,
              overflowX: 'hidden',
            }),
          }}
        />
      </div>
      <table className='company-table'>
        <thead className="table-header-new">
          <tr className='company'>
            <td className="company-logo">Bild</td>
            <td className='company-name'><SortButton name="name" sort={sort}>Företag<img className="sortBtn" src={Sort} alt="" /></SortButton></td>
            <td>Taggar</td>
            <td><SortButton name="categoryId" sort={sort}>Kategori<img className="sortBtn" src={Sort} alt="" /></SortButton></td>
            <td className='company-total-coupons-and-booklet-select'>
              <SortButton name="totalUsedCoupons" sort={sort}>Nyttjade kuponger<img className="sortBtn" src={Sort} alt="" /></SortButton>
            </td>
            {
              filteredBooklets.map((bookletobj) => (
                <td key={bookletobj._id} className="booklet-column">
                  <CompaniesTableBooklet booklet={bookletobj} />
                </td>
              ))
            }
          </tr>
        </thead>
        <tbody>
          {editing === 'new' && (
            <CompanyForm
              categories={categoriesQuery}
              types={typesQuery}
              content={content}
              onComplete={() => setEditing('')}
            />
          )}
          {
            !companiesData?.companies ? <LoadingTable loading={companiesLoading} error={companiesError} /> : (
              sortList(filteredCompanies, sort, totalUsedCoupons, categoriesQuery).map((company, index) => (
                editing === company._id
                  ? <CompanyForm key={company._id} categories={categoriesQuery} types={typesQuery} content={company} onComplete={() => setEditing('')} />
                  : (
                    <CompanyTableItem
                      key={company._id}
                      company={company}
                      categoriesQuery={categoriesQuery}
                      setEditing={setEditing}
                      companyId={company._id}
                      contactEmails={company.contactEmails ?? []}
                      checked={checked}
                      zoneId={zoneId}
                      companyIndex={index}
                    />
                  )
              ))
            )
          }
        </tbody>
      </table>
    </TableSection>
  );
}

function CompaniesTable(props) {
  return (
    <CompaniesTableProvider>
      <CompaniesTableView {...props} />
    </CompaniesTableProvider>
  );
}

CompaniesTable.propTypes = {
  zoneId: PropTypes.string.isRequired,
  onComplete: PropTypes.func
};

export default CompaniesTable;
